import React, { useCallback, useEffect, useState } from "react";
import {
  Grid,
  Stack,
  TextField,
  Button,
  Alert,
  Typography,
  Box,
  Divider,
} from "@mui/material";
import { MdVisibility, MdVisibilityOff, MdWarning } from "react-icons/md";
import dayjs from "dayjs";

import { Surface, SectionTitle } from "components/ui";
import { T, tint } from "theme/tokens";
import * as api from "../api";

const EMPTY = {
  accountHolderName: "",
  accountNumber: "",
  ifscCode: "",
  bankName: "",
  branchName: "",
};

export default function BankTab({ pharmacyId, notify }) {
  const [bank, setBank] = useState(null);
  const [bankUpdatedAt, setBankUpdatedAt] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const load = useCallback(
    async (reveal = false) => {
      setLoading(true);
      setError("");
      try {
        const res = await api.getBank(pharmacyId, { reveal });
        setBank(res.bank);
        setBankUpdatedAt(res.bankUpdatedAt);
        setRevealed(Boolean(res.revealed));
      } catch (err) {
        setError(err?.response?.data?.message || "Could not load bank details.");
      } finally {
        setLoading(false);
      }
    },
    [pharmacyId]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  const startEdit = () => {
    // Editing needs the real account number, not the masked one — otherwise
    // saving would write the asterisks back.
    api
      .getBank(pharmacyId, { reveal: true })
      .then((res) => {
        setForm({ ...EMPTY, ...(res.bank || {}) });
        setEditing(true);
      })
      .catch((err) =>
        setError(err?.response?.data?.message || "Could not open the editor.")
      );
  };

  const save = async () => {
    setSaving(true);
    setFormError("");
    try {
      await api.updateBank(pharmacyId, form);
      notify?.("Bank details saved.");
      setEditing(false);
      await load(false);
    } catch (err) {
      setFormError(
        err?.response?.data?.message || err?.message || "Could not save."
      );
    } finally {
      setSaving(false);
    }
  };

  const field = (name, label, extra = {}) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={form[name] || ""}
      onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))}
      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
      {...extra}
    />
  );

  const Row = ({ label, value, mono }) => (
    <Grid item xs={12} sm={6}>
      <Typography
        sx={{
          fontSize: "0.65rem",
          fontWeight: 600,
          color: T.subtle,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.95rem",
          color: T.ink,
          fontFamily: mono ? "monospace" : "inherit",
          fontWeight: mono ? 600 : 400,
        }}
      >
        {value || "—"}
      </Typography>
    </Grid>
  );

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}

      <Surface sx={{ p: { xs: 2, md: 3 } }}>
        <SectionTitle
          action={
            !editing && (
              <Stack direction="row" spacing={1}>
                {bank?.accountNumber && (
                  <Button
                    size="small"
                    startIcon={revealed ? <MdVisibilityOff /> : <MdVisibility />}
                    onClick={() => load(!revealed)}
                    sx={{ textTransform: "none", color: T.muted }}
                  >
                    {revealed ? "Hide" : "Reveal"}
                  </Button>
                )}
                <Button
                  size="small"
                  variant="contained"
                  onClick={startEdit}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    bgcolor: T.primary,
                    "&:hover": { bgcolor: "#013838" },
                  }}
                >
                  {bank?.accountNumber ? "Update" : "Add bank details"}
                </Button>
              </Stack>
            )
          }
        >
          Bank account
        </SectionTitle>

        {!editing && (
          <>
            {loading ? (
              <Typography sx={{ color: T.muted, fontSize: "0.85rem" }}>Loading…</Typography>
            ) : bank?.accountNumber ? (
              <>
                <Grid container spacing={2}>
                  <Row label="Account holder" value={bank.accountHolderName} />
                  <Row label="Account number" value={bank.accountNumber} mono />
                  <Row label="IFSC" value={bank.ifscCode} mono />
                  <Row label="Bank" value={bank.bankName} />
                  <Row label="Branch" value={bank.branchName} />
                  <Row
                    label="Last changed"
                    value={
                      bankUpdatedAt
                        ? dayjs(bankUpdatedAt).format("DD MMM YYYY, hh:mm A")
                        : "Unknown"
                    }
                  />
                </Grid>

                {revealed && (
                  // Reading a full account number is itself recorded — say so,
                  // so nobody is surprised to find it in the audit trail.
                  <Alert
                    severity="warning"
                    icon={<MdWarning />}
                    sx={{ mt: 2, borderRadius: 2 }}
                  >
                    The full account number is shown. This view was recorded in
                    the activity log against your account.
                  </Alert>
                )}
              </>
            ) : (
              <Typography sx={{ color: T.muted, fontSize: "0.88rem" }}>
                No bank details on file. This pharmacy cannot be paid out until
                they are added.
              </Typography>
            )}
          </>
        )}

        {editing && (
          <Stack spacing={2}>
            {formError && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {formError}
              </Alert>
            )}

            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: tint(T.warning, 0.1),
                border: `1px solid ${tint(T.warning, 0.3)}`,
              }}
            >
              <Typography sx={{ fontSize: "0.8rem", color: T.ink }}>
                Check these against a cancelled cheque or passbook before
                saving. A rejected transfer comes back; a successful transfer
                into a valid stranger's account does not.
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {field("accountHolderName", "Account holder name")}
              </Grid>
              <Grid item xs={12} md={6}>
                {field("accountNumber", "Account number", {
                  inputProps: { inputMode: "numeric" },
                  helperText: "6–20 digits",
                })}
              </Grid>
              <Grid item xs={12} md={6}>
                {field("ifscCode", "IFSC code", {
                  helperText: "e.g. HDFC0001234",
                })}
              </Grid>
              <Grid item xs={12} md={6}>
                {field("bankName", "Bank name")}
              </Grid>
              <Grid item xs={12} md={6}>
                {field("branchName", "Branch")}
              </Grid>
              <Grid item xs={12} md={6}>
                {field("reason", "Reason for this change", {
                  placeholder: "Recorded in the activity log",
                })}
              </Grid>
            </Grid>

            <Divider />

            <Typography sx={{ fontSize: "0.75rem", color: T.subtle }}>
              Saving clears any bank verification recorded against the previous
              account, so the new one has to be proved before a payout can run.
            </Typography>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                onClick={() => {
                  setEditing(false);
                  setFormError("");
                }}
                disabled={saving}
                sx={{ textTransform: "none", color: T.muted }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={save}
                disabled={saving}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  bgcolor: T.primary,
                  "&:hover": { bgcolor: "#013838" },
                }}
              >
                {saving ? "Saving…" : "Save bank details"}
              </Button>
            </Stack>
          </Stack>
        )}
      </Surface>
    </Stack>
  );
}
